import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, Grid, Paper, Typography } from "@mui/material";
import axios from "../../api";
import { useParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useCustomerId } from "context/CustomerIdContext";
import SoftBox from "components/SoftBox";
import { AxiosError } from "axios";
import { MoneyOutlined } from "@mui/icons-material";
import moment from "moment";

interface Subscriptions {
  duration_in_days: number;
  asset_type_name: string;
  subscription_id: number;
  start_date: string | Date;
  end_date: string | Date;
  asset_name: string;
  is_active: number;
  subscription_type_name: string;
  subscription_price: number;
}

interface ViewSubscriptionProps {
  /** When provided, "Add Subscription" closes current overlay and parent opens Add Subscription overlay with customer pre-filled */
  onOpenAddSubscription?: (customerId: number, customerType: string) => void;
  customerType?: string;
  /** Increment to refetch the list without changing customer id */
  refreshNonce?: number;
}

const ViewSubscription = ({
  onOpenAddSubscription,
  customerType,
  refreshNonce = 0,
}: ViewSubscriptionProps = {}) => {
  const navigate = useNavigate();
  const { fetchError } = useSnackbar();
  const contextId = useCustomerId();
  const { id: paramId } = useParams();
  const id = contextId ?? paramId;

  const [subscriptions, setSubscriptions] = useState<Subscriptions[] | null>(
    null
  );

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get(`/subscription/customer/${id}`);
      setSubscriptions(response?.data?.list);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets, refreshNonce]);

  return (
    <Card sx={{ p: 3, maxWidth: "100%", background: "#fff" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight="600">
          Manage Subscription
        </Typography>

        <Button
          variant="contained"
          sx={{
            backgroundColor: "#4aa4a4",
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            py: 1,
            "&:hover": { backgroundColor: "#3c8f8f" },
          }}
          onClick={() => {
            if (onOpenAddSubscription && id) {
              onOpenAddSubscription(Number(id), customerType ?? "customer");
            } else {
              navigate(`/add-subscription`);
            }
          }}
        >
          Add Subscription
        </Button>
      </Box>
      <SoftBox mt={2}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={12}>
            {subscriptions && subscriptions.length > 0 ? (
              subscriptions?.map((data) => {
                const formattedStartDate = data.start_date
                  ? moment(data.start_date).format("DD MMM YYYY")
                  : "N/A";
                const formattedEndDate = data.end_date
                  ? moment(data.end_date).format("DD MMM YYYY")
                  : "N/A";
                const activeLabel =
                  data.is_active === 1 ? "active" : "inactive";

                return (
                  <Paper
                    key={data.subscription_id}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 3,
                      borderRadius: "14px",
                      border: "1px solid rgba(224,224,224,0.45)",
                      backgroundColor: data.is_active ? "#F3FFFB" : "#fff",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Box display="flex" alignItems="center" gap={1.2}>
                        {/* ICON */}
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            backgroundColor: "#FFF7E6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#D99A32",
                          }}
                        >
                          <MoneyOutlined sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography
                            fontSize={17}
                            fontWeight={700}
                            color="#222"
                            sx={{ lineHeight: "20px" }}
                          >
                            {data.subscription_type_name}
                          </Typography>

                          <Typography
                            fontSize={13}
                            fontWeight={500}
                            mt={0.5}
                            color="#777"
                          >
                            {data.asset_type_name} - {data.asset_name}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          px: 1.4,
                          py: 0.5,
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "capitalize",
                          backgroundColor:
                            activeLabel === "active" ? "#E3FCEF" : "#F1F1F1",
                          color: activeLabel === "active" ? "#0A7A3D" : "#444",
                        }}
                      >
                        {activeLabel}
                      </Box>
                    </Box>
                    <Grid container spacing={2} mt={1.5}>
                      <Grid item xs={12} sm={4}>
                        <TextRow
                          label="Amount"
                          value={
                            <>
                              {'\u20B9'}{data.subscription_price}
                              <span style={{ color: "#999", marginLeft: 4 }}>
                                {' / '}{data.duration_in_days}{' Days'}
                              </span>
                            </>
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextRow
                          label="Start Date"
                          value={formattedStartDate}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextRow label="End Date" value={formattedEndDate} />
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })
            ) : (
              <Typography
                fontSize={15}
                fontWeight={600}
                color="#777"
                textAlign="center"
                mt={4}
              >
                No Subscriptions found
              </Typography>
            )}
          </Grid>
        </Grid>
      </SoftBox>
    </Card>
  );
};

const TextRow = ({ label, value }: any) => (
  <Box>
    <Typography fontSize={13} fontWeight={600} color="#828282">
      {label}
    </Typography>

    <Typography fontSize={15} mt={0.3} fontWeight={600} color="#222">
      {value}
    </Typography>
  </Box>
);

export default ViewSubscription;
